import cloneDeep from 'lodash/cloneDeep';
/**
 * Creates a flat list of items from a tree of nested items.
 *
 * @example
 *   const input = [
 *     {
 *       id: 'a',
 *       label: 'A',
 *       items: [
 *         {
 *           id: 'b',
 *           label: 'B',
 *           parent_id: 'a',
 *           items: [{ id: 'c', label: 'C', parent_id: 'b' }],
 *         },
 *       ],
 *     },
 *   ];
 *
 *   convertTreeToFlatItems(input, { childrenKey: 'items' });
 *
 *   const output = [
 *     { id: 'a', label: 'A' },
 *     { id: 'b', label: 'B', parent_id: 'a' },
 *     { id: 'c', label: 'C', parent_id: 'b' },
 *   ];
 *
 * @example
 *   const input = [
 *     {
 *       id: 'a',
 *       label: 'A',
 *       items: [
 *         {
 *           id: 'b',
 *           label: 'B',
 *           items: [{ id: 'c', label: 'C' }],
 *         },
 *       ],
 *     },
 *   ];
 *
 *   convertTreeToFlatItems(input, {
 *     childrenKey: 'items',
 *     transformItem: ({ items, ...item }, path) => ({
 *       ...item,
 *       ...(path.length ? { parent_id: path[path.length - 1].id } : {}),
 *     }),
 *   });
 *
 *   const output = [
 *     { id: 'a', label: 'A' },
 *     { id: 'b', label: 'B', parent_id: 'a' },
 *     { id: 'c', label: 'C', parent_id: 'b' },
 *   ];
 *
 * @param items The items to flatten.
 * @param config The configuration.
 */
export function convertTreeToFlatItems(items, config) {
    const { childrenKey, transformItem = (item) => Object.fromEntries(Object.entries(item).filter(([key]) => key !== childrenKey)), } = config;
    const result = [];
    /**
     * Flattens the items.
     *
     * @param items The items to flatten.
     * @param parentIdValue The value of the parent id.
     */
    const flattenItems = (items, path = []) => {
        items.forEach((item) => {
            result.push(transformItem(item, path));
            const childrenItems = item[childrenKey];
            if (childrenItems === null || childrenItems === void 0 ? void 0 : childrenItems.length) {
                flattenItems(childrenItems, path.concat([item]));
            }
        });
    };
    flattenItems(cloneDeep(items));
    return result;
}
